import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { intelligenceApi } from '../lib/adapter';
import type { AIActivity, AILead } from '../types/contracts';
import { IntelligenceShell } from '../components/IntelligenceShell';
import { LeadScore } from '../components/LeadScore';
import { JourneyPipeline } from '../components/LeadStage';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { ErrorState, Skeleton } from '../components/states';
import { CHANNEL_ICON, STAGE_LABEL, shortTime } from '../lib/format';
import type { SalesStage } from '../types/contracts';

const JOURNEY: SalesStage[] = ['new', 'contacted', 'qualified', 'demo', 'trial', 'negotiation', 'won'];

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-black tracking-widest text-gray-400">{label.toUpperCase()}</p>
      <p className="text-sm font-semibold text-gray-800 mt-0.5">{value}</p>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lead, setLead] = useState<AILead | null>(null);
  const [timeline, setTimeline] = useState<AIActivity[]>([]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    const r = await intelligenceApi.getLead(id);
    if (!r.ok) { setLoading(false); setError('message' in r ? r.message : 'حدث خطأ ما'); return; }
    setLead(r.data);
    const t = await intelligenceApi.getLeadTimeline(id);
    setLoading(false);
    if (t.ok) setTimeline(t.data);
  };
  useEffect(() => { void load(); }, [id]);

  const stageIdx = lead ? JOURNEY.indexOf(lead.stage) : -1;

  return (
    <IntelligenceShell>
      {loading ? <Skeleton rows={8} /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && lead ? (
        <div className="grid lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl bg-white border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-black text-gray-900">الملف الشخصي</h2>
                <LeadScore score={lead.score} temperature={lead.temperature} />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="الاسم" value={lead.name} />
                <Field label="النشاط التجاري" value={lead.business} />
                <Field label="الهاتف" value={lead.phone} />
                <Field label="الموقع" value={lead.location} />
                <Field label="المصدر" value={lead.source} />
                <Field label="تاريخ الإنشاء" value={shortTime(lead.created_at)} />
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                <i className={CHANNEL_ICON[lead.source]} />
                المرحلة الحالية: <b className="text-gray-800">{STAGE_LABEL[lead.stage]}</b>
                <span className="text-gray-300">|</span>
                الإجراء التالي: <b className="text-teal-700">{lead.next_action}</b>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 p-5">
              <h2 className="text-sm font-black text-gray-900 mb-4">الجدول الزمني للنشاط</h2>
              <ActivityTimeline items={timeline} />
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <h2 className="text-[10px] font-black tracking-widest text-gray-400 mb-3">مرحلة البيع</h2>
            <JourneyPipeline
              journey={JOURNEY.map((s, i) => ({
                stage: s,
                state: i < stageIdx ? 'done' : i === stageIdx ? 'current' : 'upcoming',
              }))}
            />
          </div>
        </div>
      ) : null}
    </IntelligenceShell>
  );
}
